/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { OnlineOrdersScreen } from '../../../orders/screens/OnlineOrdersScreen';
import { OrderMessagesInboxScreen } from '../../../orders/screens/OrderMessagesInboxScreen';
import { OnlineComplaintsScreen } from '../../../complaints/public-api';
import { ConsultationsScreen } from '../../../consultations/screens/ConsultationsScreen';
import { QuotesScreen } from '../../../quotes/screens/QuotesScreen';
import { AppointmentsScreen } from '../../../appointments/screens/AppointmentsScreen';
import { useHasBusinessCapability } from '../../session/hooks/useEffectiveBusinessCapabilities';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import { useModulePermission } from '../../session/hooks/useModulePermission';
import { usePendingOrdersCount } from '../../providers/PendingOrdersCountProvider';
import { SupportUnreadBadge } from '../../support/components/SupportUnreadBadge';
import { spacing } from '../../../shared/theme/spacing';
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { navigateInShell } from '../rootNavigation';

type InboxSegment = 'orders' | 'complaints' | 'messages' | 'consultations' | 'quotes' | 'appointments';

/** Route params accepted by OrdersInbox for deep-linking to a specific segment. */
export type OrdersInboxRouteParams = {
  initialSegment?: InboxSegment;
};

/** Must match MainTabs route so WebTabDataRefreshButton finds the handler. */
const ORDERS_INBOX_REFRESH_ROUTE = 'OrdersInbox';

/** testID prefix for segment chips — e.g. `orders-inbox.segment.orders`. */
const SEGMENT_TEST_ID: Record<InboxSegment, string> = {
  orders: 'orders-inbox.segment.orders',
  complaints: 'orders-inbox.segment.complaints',
  messages: 'orders-inbox.segment.messages',
  consultations: 'orders-inbox.segment.consultations',
  quotes: 'orders-inbox.segment.quotes',
  appointments: 'orders-inbox.segment.appointments',
};

const SEGMENT_LABELS: Record<InboxSegment, string> = {
  orders: 'Pedidos',
  complaints: 'Reclamos',
  messages: 'Mensajes',
  consultations: 'Consultas',
  quotes: 'Cotizaciones',
  appointments: 'Turnos',
};

/**
 * El título del header es siempre el nombre del módulo en el nav (Pedidos/Bandeja) —
 * un solo nombre para la superficie. El segmento activo aparece como kicker y en el tab.
 */
const SEGMENT_META: Record<InboxSegment, { kicker: string; meta?: string }> = {
  orders: { kicker: 'Tienda online', meta: 'Los que llegan de tu tienda.' },
  complaints: { kicker: 'Reclamos', meta: 'Los reclamos que dejan tus clientes.' },
  messages: { kicker: 'Mensajes', meta: 'Los mensajes de tus clientes.' },
  consultations: { kicker: 'Consultas', meta: 'Las consultas de tus clientes.' },
  quotes: { kicker: 'Cotizaciones', meta: 'Los presupuestos que te piden tus clientes.' },
  appointments: { kicker: 'Turnos', meta: 'Los turnos que reservan tus clientes.' },
};

/**
 * Wrapper con segmentos sobre pantallas existentes (Pedidos | Reclamos | Mensajes | Consultas | Turnos).
 * No fusiona UI: monta la pantalla correspondiente.
 */
export function OrdersInboxScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { width } = useWindowDimensions();
  const ctx = useNavVisibilityContext();
  const canViewOrders = useModulePermission('Orders', 'view');
  const canViewStorefront = useModulePermission('BranchSettings', 'view');
  const canViewAgenda = useModulePermission('Agenda', 'view');
  const hasAppointments = useHasBusinessCapability('appointments');
  const hasServiceQuotes = useHasBusinessCapability('service_quotes');
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const {
    pendingOrders,
    openComplaints,
    unreadMessages,
    unreadConsultations,
    unreadQuotes,
    refresh: refreshInboxCounts,
  } = usePendingOrdersCount();

  const segments = useMemo(() => {
    const list: InboxSegment[] = [];
    if (hasDelivery && canViewOrders) list.push('orders');
    if (ctx.publicStoreFeaturesAllowed && canViewStorefront) {
      list.push('complaints');
      list.push('messages');
    }
    if (ctx.pharmacyConsultationsAllowed && canViewStorefront) list.push('consultations');
    if (hasServiceQuotes && canViewStorefront) list.push('quotes');
    if (hasAppointments && canViewAgenda) list.push('appointments');
    // Fallback: never leave inbox empty if public store is on
    if (list.length === 0 && ctx.publicStoreFeaturesAllowed && canViewOrders) list.push('orders');
    return list;
  }, [
    canViewAgenda,
    canViewOrders,
    canViewStorefront,
    ctx.pharmacyConsultationsAllowed,
    ctx.publicStoreFeaturesAllowed,
    hasAppointments,
    hasDelivery,
    hasServiceQuotes,
  ]);

  const segmentBadge: Record<InboxSegment, number> = {
    orders: pendingOrders ?? 0,
    complaints: openComplaints ?? 0,
    messages: unreadMessages ?? 0,
    consultations: unreadConsultations ?? 0,
    quotes: unreadQuotes ?? 0,
    appointments: 0,
  };

  const defaultSegment: InboxSegment = useMemo(() => {
    if (hasAppointments && !hasDelivery && !hasServiceQuotes) return 'appointments';
    const attentionOrder: InboxSegment[] = [
      'orders',
      'complaints',
      'messages',
      'consultations',
      'quotes',
      'appointments',
    ];
    for (const key of attentionOrder) {
      if (!segments.includes(key)) continue;
      const n =
        key === 'orders'
          ? (pendingOrders ?? 0)
          : key === 'complaints'
            ? (openComplaints ?? 0)
            : key === 'messages'
              ? (unreadMessages ?? 0)
              : key === 'consultations'
                ? (unreadConsultations ?? 0)
                : key === 'quotes'
                  ? (unreadQuotes ?? 0)
                  : 0;
      if (n > 0) return key;
    }
    return segments[0] ?? 'orders';
  }, [
    hasAppointments,
    hasDelivery,
    hasServiceQuotes,
    openComplaints,
    pendingOrders,
    segments,
    unreadConsultations,
    unreadMessages,
    unreadQuotes,
  ]);
  // Deep-link support: if the route provides `initialSegment` and it's a valid
  // segment for this org, use it as the initial active segment. Falls back to
  // the attention-order default when the param is absent or invalid.
  const initialSegmentParam = route.params?.initialSegment as InboxSegment | undefined;
  const [segment, setSegment] = useState<InboxSegment | null>(
    initialSegmentParam ?? null,
  );
  const active = segments.includes(segment ?? defaultSegment)
    ? (segment ?? defaultSegment)
    : defaultSegment;

  useEffect(() => {
    void refreshInboxCounts();
  }, [refreshInboxCounts]);

  // Re-apply initialSegment when the route param changes (deep-link to the same
  // screen with a different segment). Only applies if the segment is valid for
  // this org; otherwise the default attention-order logic takes over.
  useEffect(() => {
    const next = route.params?.initialSegment as InboxSegment | undefined;
    if (next && segments.includes(next)) {
      setSegment(next);
    }
  }, [route.params?.initialSegment, segments]);

  const segmentTabsNode =
    segments.length > 1 ? (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.segmentScroll}
        contentContainerStyle={styles.segmentRow}
        accessibilityRole="tablist"
      >
        {segments.map((key) => {
          const selected = key === active;
          const badge = segmentBadge[key];
          return (
            <TouchableOpacity
              key={key}
              style={[styles.segment, selected && styles.segmentSelected]}
              onPress={() => setSegment(key)}
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              accessibilityLabel={
                badge > 0 ? `${SEGMENT_LABELS[key]}, ${badge}` : SEGMENT_LABELS[key]
              }
              testID={SEGMENT_TEST_ID[key]}
            >
              <View style={styles.segmentInner}>
                <Text style={[styles.segmentLabel, selected && styles.segmentLabelSelected]}>
                  {SEGMENT_LABELS[key]}
                </Text>
                {badge > 0 ? (
                  <SupportUnreadBadge
                    count={badge}
                    variant="inline"
                    style={selected ? styles.badgeOnSelected : undefined}
                  />
                ) : null}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    ) : null;

  const headerCopy = SEGMENT_META[active];
  // Misma regla que `resolveNavItemLabel('ordersInbox')` en navigationManifest:
  // "Pedidos" solo cuando pedidos online es la única pestaña posible;
  // agenda sin delivery o más de un segmento → "Bandeja".
  const inboxTitle =
    (hasAppointments && !hasDelivery) || segments.length > 1 ? 'Bandeja' : 'Pedidos';

  const openHistory = useCallback(() => {
    navigateInShell(navigation, 'OrdersHistory');
  }, [navigation]);

  // Botón Historial como trailing del header (solo Pedidos, solo desktop)
  const isWide = width >= 768;
  const deskHistoryBtn = (
    <TouchableOpacity
      style={styles.deskHistoryBtn}
      onPress={openHistory}
      accessibilityRole="button"
      accessibilityLabel="Historial de pedidos"
      testID="orders.filter.history"
    >
      <Text style={styles.deskHistoryBtnText}>Historial</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.root}>
      <View style={styles.chromeWrap}>
        <TicketModuleHeader
          kicker={headerCopy.kicker}
          title={inboxTitle}
          meta={headerCopy.meta}
          testID="orders-inbox.header.sheet"
          trailing={active === 'orders' && isWide ? deskHistoryBtn : undefined}
        />
        {segmentTabsNode ? (
          <View style={styles.segmentTabsWrap}>{segmentTabsNode}</View>
        ) : null}
      </View>
      <View style={styles.body}>
        {active === 'orders' ? <OnlineOrdersScreen /> : null}
        {active === 'complaints' ? (
          <OnlineComplaintsScreen
            refreshRouteName={ORDERS_INBOX_REFRESH_ROUTE}
            initialFilter={(openComplaints ?? 0) > 0 ? 'open' : 'all'}
          />
        ) : null}
        {active === 'messages' ? (
          <OrderMessagesInboxScreen refreshRouteName={ORDERS_INBOX_REFRESH_ROUTE} />
        ) : null}
        {active === 'consultations' ? (
          <ConsultationsScreen
            refreshRouteName={ORDERS_INBOX_REFRESH_ROUTE}
            initialFilter={(unreadConsultations ?? 0) > 0 ? 'unread' : 'all'}
          />
        ) : null}
        {active === 'quotes' ? (
          <QuotesScreen
            refreshRouteName={ORDERS_INBOX_REFRESH_ROUTE}
            initialFilter={(unreadQuotes ?? 0) > 0 ? 'unread' : 'all'}
          />
        ) : null}
        {active === 'appointments' ? (
          <AppointmentsScreen refreshRouteName={ORDERS_INBOX_REFRESH_ROUTE} />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  segmentScroll: { flexGrow: 0 },
  chromeWrap: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    gap: 10,
  },
  segmentTabsWrap: {
    paddingBottom: spacing.xs,
  },
  segmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  segment: {
    flexShrink: 0,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  segmentSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  segmentInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  segmentLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  segmentLabelSelected: {
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
  badgeOnSelected: {
    // Keep badge readable on gold selected tab.
    opacity: 0.95,
  },
  deskHistoryBtn: {
    height: 42,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  deskHistoryBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  body: { flex: 1, minHeight: 0 },
});
